import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { minimalOrgSchema, orgFeedbackTypeSchema, orgSchema } from './org-shared.js';
import { plainScheduleSchema } from './schedule.js';

export const schoolStatusSchema = z.enum(['active', 'inactive']);
export const SchoolStatus = schoolStatusSchema.enum;
export type SchoolStatus = z.infer<typeof schoolStatusSchema>;

export const schoolSchema = z.object({
  id: z.string(),
  type: z.literal('school'),
  name: z.string(),
  orgId: z.string(),
  schoolStatus: schoolStatusSchema,
  attendancePercentLast14Days: z.number().nullable(),
  totalStudents: z.number().int().nullable(),
  studentsInfoUpdatedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  totalFutureSessions: z.array(z.number().int()).nullable(),
  nextSessionPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  sessionInfoUpdatedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  address: z.string().nullable(),
  email: z.string().email().nullable(),
  needSpanishFeedback: z.boolean().nullable(),
  phone: z.string().nullable(),
  schoolAdminName: z.string().nullable(),
  totalSeats: z.number().int(),
  canSendSchoolParentReminders: z.boolean().nullable(),
  canSendFamilyParentReminders: z.boolean().nullable(),
  /** Denormalized from `Org.feedbackType`. Null when the org was not loaded with the school. */
  orgFeedbackType: orgFeedbackTypeSchema.nullable(),
  edlinkId: z.string().nullable().optional(),
});
export type School = z.infer<typeof schoolSchema>;

// Minimal school schema with only id, name, status used for APIs
// that are user-facing. Students/coaches should not see full school data like attendance percent,
// total students, etc.
export const minimalSchoolSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: schoolStatusSchema,
  orgId: z.string(),
});
export type MinimalSchool = z.infer<typeof minimalSchoolSchema>;

export const schoolWithOrgSchema = schoolSchema.extend({
  org: orgSchema,
});
export type SchoolWithOrg = z.infer<typeof schoolWithOrgSchema>;

export const minimalSchoolWithOrgSchema = minimalSchoolSchema.extend({
  org: orgSchema,
});
export type MinimalSchoolWithOrg = z.infer<typeof minimalSchoolWithOrgSchema>;

// View-specific contract for the admin schools list (GET /schools).
// Only the columns the table renders — see apps/client/.../orgs/schools/index.tsx.
export const schoolListOrgSchema = minimalOrgSchema.extend({
  timeZone: z.string(),
});
export type SchoolListOrg = z.infer<typeof schoolListOrgSchema>;

export const schoolListRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  orgId: z.string(),
  schoolStatus: schoolStatusSchema,
  org: schoolListOrgSchema,
  schedules: z.lazy(() => z.array(plainScheduleSchema())),
  totalStudents: z.number().int().nullable(),
  totalFutureSessions: z.array(z.number().int()).nullable(),
  nextSessionPlainDate: z.instanceof(Temporal.PlainDate).nullable(),
  attendancePercentLast14Days: z.number().nullable(),
  edlinkId: z.string().nullable(),
});
export type SchoolListRow = z.infer<typeof schoolListRowSchema>;
